import React, { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import './RelationCreator.css';
import { initializeRelationCreatorFeatures, enhanceAutoComplete } from './RelationCreatorHelpers';

const RelationCreator = ({
  selectedNode,
  nodes,
  setNodes,
  targetNode,
  setTargetNode,
  targetNodeSearch,
  setTargetNodeSearch,
  relationshipType,
  setRelationshipType,
  customRelationshipType,
  setCustomRelationshipType,
  relationships,
  addRelationship,
  loading,
  currentPage,
  setCurrentPage,
  apiClient,
}) => {
  const { t } = useTranslation();

  // Filtrage des nœuds cibles disponibles (robuste aux valeurs undefined)
  const filteredTargetNodes = (nodes || []).filter((node) => {
    const nodeName = node?.name || '';
    const search = (targetNodeSearch || '').toLowerCase();
    return (
      nodeName &&
      nodeName !== (selectedNode?.name || '') &&
      nodeName.toLowerCase().includes(search)
    );
  });

  // Auto-select first filtered result if user types and no node is selected
  useEffect(() => {
    if (
      targetNodeSearch &&
      filteredTargetNodes.length > 0 &&
      (targetNode === "" || !filteredTargetNodes.some(n => n.name === targetNode))
    ) {
      setTargetNode(filteredTargetNodes[0].name);
    }
    // If search is empty and selected node is no longer in the list, reset
    if (
      (!targetNodeSearch || filteredTargetNodes.length === 0) &&
      targetNode !== "" &&
      !filteredTargetNodes.some(n => n.name === targetNode)
    ) {
      setTargetNode("");
    }
  }, [targetNodeSearch, nodes, selectedNode, setTargetNode]);

  // Initialiser les fonctionnalités UX améliorées
  useEffect(() => {
    const cleanup = initializeRelationCreatorFeatures(relationships, t);
    enhanceAutoComplete();

    return cleanup;
  }, [relationships, t]);

  return (
    <div id="section-create-relation" className="relationship-creator-container">
      <div className="relationship-header">
        <div className="relationship-summary-card">
          <div className="relationship-indicator">
            <span className="relationship-icon">🔗</span>
            <div className="relationship-info">
              <h3 className="relationship-title">{t('relationCreator.createRelation')}</h3>
              <p className="relationship-description">
                {t('relationCreator.connectNodesDescription')}
              </p>
            </div>
          </div>
          {/* Nouveau: Barre de statut intelligente */}
          <div className="relationship-status-bar">
            <div className="status-item">
              <div className={`status-icon ${selectedNode && targetNode && relationshipType ? 'valid' : targetNode ? 'warning' : 'error'}`}></div>
              <span>
                {selectedNode && targetNode && relationshipType
                  ? t('relationCreator.readyToCreate')
                  : targetNode
                    ? t('relationCreator.relationTypeRequired')
                    : t('relationCreator.targetNodeRequired')
                }
              </span>
            </div>
            <div className="quick-stats">
              <div className="stat02-item">
                <span>⚡</span>
                <span>{filteredTargetNodes.length} {t('relationCreator.nodesAvailable')}</span>
              </div>
              <div className="stat02-item">
                <span>🎯</span>
                <span>{relationships?.length || 0} {t('relationCreator.existingRelations')}</span>
              </div>
            </div>
          </div>
          {/* Nouveau: Indicateur de shortcuts */}
          <div className="keyboard-hint">
            <kbd>Ctrl</kbd> + <kbd>?</kbd> {t('relationCreator.forShortcuts')}
          </div>
        </div>
      </div>

      <div className="relationship-body">
        <div className="relationship-form">
          <div className={`relationship-visualization ${selectedNode && targetNode && relationshipType ? 'has-preview' : ''}`}>
            {/* Nouveau: Indicateur de validation */}
            <div className={`validation-indicator ${selectedNode && targetNode && relationshipType ? 'valid' : targetNode ? 'pending' : 'invalid'}`}>
              {selectedNode && targetNode && relationshipType ? '✓' : targetNode ? '⚠' : '✗'}
            </div>

            <div className="source-node">
              <div className={`node-circle ${selectedNode ? 'connecting' : ''}`}>
                <span className="node-icon">{selectedNode ? "📄" : "❓"}</span>
                {selectedNode && (
                  <div className="node-suggestions">3</div>
                )}
              </div>
              <div className="node-label">
                {selectedNode ? selectedNode.name : t('relationCreator.noNodeSelected')}
              </div>
            </div>

            <div className="relationship-arrow">
              <div className={`arrow-line ${selectedNode && targetNode && relationshipType ? 'active' : ''}`}></div>
              <div className={`arrow-type ${selectedNode && targetNode && relationshipType ? 'preview' : ''}`}>
                {relationshipType || t('relationCreator.relationshipType')}
              </div>
            </div>

            <div className="target-node">
              <div className="node-circle">
                <span className="node-icon">📄</span>
              </div>
              <div className="node-label">
                {targetNode || t('relationCreator.chooseTargetNode')}
              </div>
            </div>
          </div>

          <div className="form-controls">
            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">🔄</span>
                {t('relationCreator.relationshipType')}
                <span className={`field-indicator ${relationshipType ? 'completed' : 'required'}`}>
                  {relationshipType ? t('relationCreator.completed') : t('relationCreator.required')}
                </span>
              </label>
              <select
                className="modern-select"
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value)}
                required
              >
                <option value="">{t('relationCreator.selectRelationshipType')}</option>
                <option value="USES">USES</option>
                <option value="DEPENDS_ON">DEPENDS_ON</option>
                <option value="CONTAINS">CONTAINS</option>
                <option value="IMPLEMENTS">IMPLEMENTS</option>
                <option value="TESTS">TESTS</option>
                <option value="DOCUMENTS">DOCUMENTS</option>
                <option value="CUSTOM">CUSTOM</option>
              </select>
              {relationshipType === "CUSTOM" && (
                <div className="custom-relationship-input">
                  <input
                    type="text"
                    placeholder={t('relationCreator.customRelationshipPlaceholder')}
                    value={customRelationshipType}
                    onChange={(e) => setCustomRelationshipType(e.target.value)}
                    className="modern-input"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('relationCreator.customRelationshipNote')}
                  </p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <span className="label-icon">🔗</span>
                {t('relationCreator.nodeTypes')}
              </label>
              <select
                className="nodes-filter-select"
                onChange={(e) => {
                  const filterType = e.target.value;
                  setCurrentPage(1);
                  if (filterType === "all") {
                    apiClient.get(`/nodes`)
                      .then((response) => setNodes(response.data))
                      .catch((error) =>
                        console.error(t('relationCreator.errorLoadingNodes'), error)
                      );
                  } else if (filterType) {
                    apiClient.get(`/nodes`)
                      .then((response) => {
                        setNodes(response.data.filter((node) => node.type === filterType));
                      })
                      .catch((error) =>
                        console.error(t('relationCreator.errorLoadingNodes'), error)
                      );
                  }
                }}
              >
                <option value="all">{t('relationCreator.allTypes')}</option>
                <option value="Feature">Feature</option>
                <option value="Module">Module</option>
                <option value="API">API</option>
                <option value="Database">Database</option>
                <option value="Service">Service</option>
                <option value="User">User</option>
              </select>
              <label className="form-label">
                <span className="label-icon">🎯</span>
                {t('relationCreator.targetNode')}
                <span className={`field-indicator ${targetNode ? 'completed' : 'required'}`}>
                  {targetNode ? t('relationCreator.completed') : t('relationCreator.required')}
                </span>
              </label>
              <div className="target-select-container">
                {/* Nouveau: Compteur de correspondances */}
                {targetNodeSearch && (
                  <div className="match-counter">
                    {filteredTargetNodes.length}
                  </div>
                )}
                <input
                  type="text"
                  placeholder={t('relationCreator.searchTargetNode')}
                  value={targetNodeSearch}
                  onChange={e => setTargetNodeSearch(e.target.value)}
                  className="nodes-search-input py-1 px-2 border rounded w-full mb-2"
                  style={{ maxWidth: 320 }}
                />
                <select
                  className={`target-select ${filteredTargetNodes.length > 0 ? 'has-suggestions' : ''}`}
                  value={targetNode}
                  onChange={(e) => setTargetNode(e.target.value)}
                >
                  <option value="">{t('relationCreator.selectTargetNode')}</option>
                  {filteredTargetNodes.map((node) => (
                    <option key={node.name} value={node.name}>
                      {node.name} ({node.type})
                    </option>
                  ))}
                </select>

                {/* Nouveau: Prévisualisation du nœud sélectionné */}
                {targetNode && (
                  <div className="node-preview show">
                    <div className="preview-content">
                      <div className="preview-icon">📄</div>
                      <div className="preview-info">
                        <div className="preview-name">{targetNode}</div>
                        <div className="preview-meta">
                          {t('relationCreator.type')} {filteredTargetNodes.find(n => n.name === targetNode)?.type || t('relationCreator.unknown')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="relationship-actions">
              <button
                className={`create-relationship-btn ${!selectedNode || !relationshipType || !targetNode
                    ? "disabled"
                    : loading
                      ? "processing"
                      : "ready"
                  }`}
                onClick={addRelationship}
                disabled={loading || !selectedNode || !relationshipType || !targetNode}
              >
                {loading ? (
                  <>
                    <span className="btn-spinner"></span>
                    <span>{t('relationCreator.creating')}</span>
                    <div className="btn-time-estimate">{t('relationCreator.estimatedTime')}</div>
                  </>
                ) : (
                  <>
                    <span className="btn-icon">🔗</span>
                    <span>{t('relationCreator.createRelation')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelationCreator;
